/**
 * The mixer (GDD §9; judgment call 50): music and effects buses into a compressor, then the master, which mute
 * silences. The music passes through a duck that a lost jet pulls down for a moment; the title demo plays its
 * effects quietly (judgment call 48).
 */
import { sliderGain } from './tuning.ts';
import { glide, silentGain } from './voices.ts';

/** Bus levels at full slider: the music sits under the effects. */
const MUSIC_LEVEL = 0.75;
const EFFECTS_LEVEL = 0.9;
/** Effects level while the title demo plays. */
export const DEMO_LEVEL = 0.45;

export interface Mixer {
  readonly master: GainNode;
  readonly compressor: DynamicsCompressorNode;
  readonly limiter: DynamicsCompressorNode;
  readonly music: GainNode;
  readonly duck: GainNode;
  readonly effects: GainNode;
  /** Targets last set (tests read these; reading an AudioParam's value would lag its automation). */
  masterTarget: number;
  musicTarget: number;
  effectsTarget: number;
  demo: boolean;
  effectsSlider: number;
}

export function createMixer(ctx: BaseAudioContext): Mixer {
  // A brick-wall limiter last, so nothing ever clips.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.1;
  limiter.connect(ctx.destination);
  const master = silentGain(ctx, limiter, 1);
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -12;
  compressor.knee.value = 12;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.004;
  compressor.release.value = 0.25;
  compressor.connect(master);
  const duck = silentGain(ctx, compressor, 1);
  const music = silentGain(ctx, duck, MUSIC_LEVEL * sliderGain(0.8));
  const effects = silentGain(ctx, compressor, EFFECTS_LEVEL * sliderGain(0.9));
  return {
    master,
    compressor,
    limiter,
    music,
    duck,
    effects,
    masterTarget: 1,
    musicTarget: music.gain.value,
    effectsTarget: effects.gain.value,
    demo: false,
    effectsSlider: 0.9,
  };
}

function applyEffects(m: Mixer, now: number): void {
  const v = EFFECTS_LEVEL * sliderGain(m.effectsSlider) * (m.demo ? DEMO_LEVEL : 1);
  if (v === m.effectsTarget) return;
  m.effectsTarget = v;
  glide(m.effects.gain, now, v, 0.08);
}

/** Settings: music and effects sliders (0–1) and mute. */
export function setVolumes(m: Mixer, now: number, music: number, effects: number, mute: boolean): void {
  const master = mute ? 0 : 1;
  if (master !== m.masterTarget) {
    m.masterTarget = master;
    glide(m.master.gain, now, master, 0.03);
  }
  const mu = MUSIC_LEVEL * sliderGain(music);
  if (mu !== m.musicTarget) {
    m.musicTarget = mu;
    glide(m.music.gain, now, mu, 0.05);
  }
  m.effectsSlider = effects;
  applyEffects(m, now);
}

/** The title demo's quieter effects. */
export function setDemo(m: Mixer, now: number, demo: boolean): void {
  if (demo === m.demo) return;
  m.demo = demo;
  applyEffects(m, now);
}

/** Pulls the music down for a moment (a lost jet). */
export function duck(m: Mixer, now: number): void {
  const g = m.duck.gain;
  g.cancelScheduledValues(now);
  g.setTargetAtTime(0.35, now, 0.05);
  g.setTargetAtTime(1, now + 0.9, 0.8);
}
